import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";
import { French } from "flatpickr/dist/l10n/fr.js";
import { english as English } from "flatpickr/dist/l10n/default.js";
import arrowBlob from "@/images/theme/arrow.svg";

const encoded = arrowBlob.split(",")[1];
const svgText = decodeURIComponent(encoded);

const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
const arrowElement = doc.documentElement;
arrowElement.classList.remove("tw:fill-white");
const nextArrow = arrowElement.outerHTML;
arrowElement.classList.add("tw:rotate-180");
const prevArrow = arrowElement.outerHTML;

import "flatpickr/dist/flatpickr.min.css";

const customFormatters = {
  "cM.": (date, locale) => {
    const month = locale.months.longhand[date.getMonth()];
    if (month.length > 3) {
      return (month.slice(0, 3) + ".").toLowerCase();
    }
    return month.toLowerCase();
  }
};

export default class extends Controller {
  static targets = [
    "input",
    "modal",
    "dateDisplay",
    "startDate",
    "endDate",
    "cartCount",
    "warnModal",
    "selectDatesToast",
    "toggle",
    "panel"
  ];
  static values = {
    locale: String,
    arrowPrevious: String,
    arrowNext: String,
    instance: String,
    userLoggedIn: Boolean
  };

  connect() {
    this.opened = false;
    this.toastTimer = null;
    this.isSmallResolution = window.innerWidth > 700;
    this.initFlatpickr();

    const mediaQuery = window.matchMedia("(min-width: 700px)");
    const handleMediaChange = (e) => {
      if (this.isSmallResolution !== e.matches) {
        this.isSmallResolution = e.matches;
        this.initFlatpickr();
      }
    };
    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener("change", handleMediaChange);

    this.repositionPanel = () => this.positionPanel();
    window.addEventListener("resize", this.repositionPanel);
    this.positionPanel();
  }

  disconnect() {
    window.removeEventListener("resize", this.repositionPanel);
    this.fp?.destroy();
    this.fp = null;
    delete window.flatpickrInstances?.[this.instanceValue];
  }

  positionPanel() {
    if (!this.hasPanelTarget) return;
    const toggle = this.toggleTargets.find((element) => element.offsetParent !== null);
    const icon = toggle?.querySelector("svg");
    if (!icon) return;

    const iconBox = icon.getBoundingClientRect();
    if (!iconBox.width) return;

    const right = document.documentElement.clientWidth - (iconBox.left + iconBox.width / 2);

    this.panelTarget.style.setProperty("--dx-calendar-anchor", `${right}px`);
  }

  // Fires when the header markup is replaced with a signed-in one without a
  // full reload, so the calendar becomes available without a second visit.
  userLoggedInValueChanged() {
    if (this.fp || !this.userLoggedInValue) return;

    this.initFlatpickr();
  }

  initFlatpickr() {
    const el = this.hasInputTarget ? this.inputTarget : this.element;
    if (this.fp) {
      this.fp.destroy();
      this.fp = null;
    }

    // The date picker is only reachable once signed in: both header triggers
    // are rendered behind user_signed_in?, and date_range/add_to_cart gate
    // their calls on hasUser. Building a two-month calendar (locale parsing,
    // DOM) on every anonymous page load is pure waste, so bail out early and
    // leave no instance behind: the consumers already handle its absence.
    // Sign-in ends with a full page load (Turbo.StreamActions.redirect_to sets
    // window.location.href), so the header comes back with the value set and
    // the calendar builds then; userLoggedInValueChanged covers the case where
    // only the header markup is swapped. Checked by value rather than by
    // has...Value so a header rendering the attribute as "false" stays gated.
    if (!this.userLoggedInValue) {
      delete window.flatpickrInstances?.[this.instanceValue];
      return;
    }

    // Spread before assigning: French/English are shared module objects, and
    // mutating them would leak the separator into every other importer.
    const localeOptions = { ...(this.localeValue === "fr" ? French : English), rangeSeparator: " - " };

    const baseOptions = {
      allowInput: true,
      clickOpens: false,
      inline: true,
      openOnFocus: false,
      mode: "range",
      appendTo: this.modalTarget,
      showMonths: this.isSmallResolution ? 2 : 1,
      locale: localeOptions,
      dateFormat: "d cM.",
      formatDate: (date, format, locale) => {
        return format
          .split(" ")
          .map((token) => {
            if (customFormatters[token]) {
              return customFormatters[token](date, locale);
            }
            return flatpickr.formatDate(date, token, locale);
          })
          .join(" ");
      },
      minDate: "today",
      utc: true,
      prevArrow: prevArrow,
      nextArrow: nextArrow
    };

    // Empty inputs used to reach flatpickr as Invalid Date objects (truthy),
    // logging "Invalid date provided" on every page load with blank
    // start_date/end_date params.
    if (this.startDateTarget.value && this.endDateTarget.value) {
      const startDate = new Date(this.startDateTarget.value).fp_incr(1);
      const endDate = new Date(this.endDateTarget.value).fp_incr(1);
      if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
        baseOptions["defaultDate"] = [startDate, endDate];
      }
    }

    this.fp = flatpickr(el, baseOptions);

    this.updateDisplay();

    window.flatpickrInstances ||= {};
    window.flatpickrInstances[this.instanceValue] = {
      open: this.open.bind(this),
      openToast: this.openSelectDatesToast.bind(this),
      hasUser: this.userLoggedInValue,
      instance: this.fp
    };
  }

  updateDisplay() {
    this.dateDisplayTargets.forEach((el) => (el.textContent = this.inputTarget.value));
  }

  openSelectDatesToast() {
    window.dxNavigation?.showHeader();
    this.element.classList.add("active-toast");
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.element.classList.remove("active-toast");
      this.toastTimer = null;
    }, 5000);
  }

  openWarnModal() {
    this.warnModalTarget.classList.add("tw:!block", "tw:!opacity-100");
  }

  closeWarnModal() {
    this.warnModalTarget.classList.remove("tw:!block", "tw:!opacity-100");
  }

  open(forceOpen = false) {
    window.dxNavigation?.showHeader();
    this.positionPanel();
    this.element.classList.remove("active-toast");
    if (+this.cartCountTarget.textContent > 0 && !forceOpen && this.hasWarnModalTarget) {
      this.openWarnModal();
      return;
    }
    this.opened = true;
    this.fp.open();
    this.element.classList.add("active");
  }
  close() {
    this.opened = false;
    this.fp.close();
    this.element.classList.remove("active");
    this.updateDisplay();
    const rawDates = this.fp.selectedDates;
    if (rawDates.length === 0) return;
    const formatter = new Intl.DateTimeFormat("en-CA");
    this.startDateTarget.value = formatter.format(rawDates[0]);
    this.endDateTarget.value = formatter.format(rawDates[1]);
    this.endDateTarget.form.submit();
  }
  toggle() {
    this.opened ? this.close() : this.open();
  }
  clear() {
    this.fp?.clear();
    this.fp.selectedDates = [];
    this.inputTarget.value = null;
    this.startDateTarget.value = null;
    this.endDateTarget.value = null;
    this.element.classList.remove("active");
    this.endDateTarget.form.submit();
    this.updateDisplay();
  }
}
